import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import PasswordField from "../components/auth/PasswordField";
import PasswordStrengthChecklist from "../components/auth/PasswordStrengthChecklist";
import SocialSignInButtons from "../components/auth/SocialSignInButtons";
import TurnstileCaptcha from "../components/auth/TurnstileCaptcha";
import { fetchCaptchaConfig, register } from "../api/auth";
import { selectIsAuthenticated } from "../features/auth/authSlice";
import { evaluatePassword } from "../utils/passwordPolicy";
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from "../legalVersions";

const isProductionBuild = process.env.NODE_ENV === "production";

const safeReturnTo = (value) => {
  const trimmed = String(value || "").trim();

  return trimmed.startsWith("/") && !trimmed.startsWith("//") ? trimmed : "/dashboard";
};

const inviteReturnTo = (searchParams) => {
  const explicitReturnTo = searchParams.get("returnTo");
  if (explicitReturnTo) return safeReturnTo(explicitReturnTo);

  const nightId = Number(searchParams.get("nightId"));
  if (Number.isInteger(nightId) && nightId > 0) {
    return `/invite/gathering/night/${nightId}`;
  }

  return "/dashboard";
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptLegalTerms, setAcceptLegalTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState(false);
  const [captchaConfig, setCaptchaConfig] = useState({
    enabled: isProductionBuild,
    loading: true,
    siteKey: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const returnTo = inviteReturnTo(searchParams);
  const passwordPolicy = evaluatePassword(password, { username, email });
  const captchaRequired = Boolean(captchaConfig.enabled);
  const captchaUnavailable = captchaRequired && !captchaConfig.siteKey;
  const captchaNotReady =
    captchaConfig.loading ||
    captchaUnavailable ||
    (captchaRequired && !captchaToken);
  const submitDisabled = useMemo(
    () => loading || !passwordPolicy.isValid || captchaNotReady,
    [captchaNotReady, loading, passwordPolicy.isValid]
  );

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
      return undefined;
    }

    let cancelled = false;

    fetchCaptchaConfig()
      .then((config) => {
        if (cancelled) return;

        setCaptchaConfig({
          enabled: Boolean(config?.enabled),
          loading: false,
          siteKey: config?.siteKey || "",
        });
      })
      .catch(() => {
        if (cancelled) return;

        setCaptchaConfig({
          enabled: isProductionBuild,
          loading: false,
          siteKey: "",
        });
        setCaptchaError(isProductionBuild);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, navigate, returnTo]);

  const handleCaptchaError = useCallback(() => {
    setCaptchaError(true);
  }, []);
  const handleCaptchaTokenChange = useCallback((token) => {
    setCaptchaToken(token);
    setCaptchaError(false);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("authRegister.passwordMismatch"));
      return;
    }

    if (!passwordPolicy.isValid) {
      setError(t("authRegister.passwordPolicy.error"));
      return;
    }

    if (!acceptLegalTerms) {
      setError(t("authRegister.legalConsentRequired"));
      return;
    }

    if (captchaConfig.loading) {
      setError(t("authRegister.captcha.loading"));
      return;
    }

    if (captchaUnavailable) {
      setCaptchaError(true);
      setError(t("authRegister.captcha.unavailable"));
      return;
    }

    if (captchaRequired && !captchaToken) {
      setCaptchaError(true);
      setError(t("authRegister.captcha.error"));
      return;
    }

    setLoading(true);
    try {
      const data = await register({
        acceptLegalTerms,
        captchaToken,
        email,
        password,
        returnTo,
        username,
      });
      const params = new URLSearchParams({
        sent: "1",
        email,
      });
      params.set("returnTo", returnTo);

      if (data.verificationUrl) {
        params.set("verificationUrl", data.verificationUrl);
      }

      navigate(`/verify-email?${params.toString()}`);
    } catch (err) {
      setError(err.response?.data?.error || t("authRegister.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title={t("authRegister.title")}
      subtitle={t("authRegister.subtitle")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        <TextField
          label={t("login.username")}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          required
          fullWidth
        />
        <TextField
          label={t("authRegister.email")}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
          fullWidth
        />
        <PasswordField
          label={t("login.password")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
          fullWidth
        />
        <PasswordStrengthChecklist
          password={password}
          username={username}
          email={email}
        />
        <PasswordField
          label={t("authRegister.confirmPassword")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          required
          fullWidth
        />

        <TurnstileCaptcha
          error={captchaError}
          onError={handleCaptchaError}
          onTokenChange={handleCaptchaTokenChange}
          siteKey={captchaConfig.siteKey}
        />

        {captchaUnavailable && (
          <Alert severity="error">{t("authRegister.captcha.unavailable")}</Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <FormControlLabel
          control={
            <Checkbox
              checked={acceptLegalTerms}
              onChange={(event) => setAcceptLegalTerms(event.target.checked)}
              required
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {t("authRegister.legalConsentPrefix")}{" "}
              <Link component={RouterLink} to="/terms">
                {t("legalLinks.terms")}
              </Link>{" "}
              {t("authRegister.legalConsentAnd")}{" "}
              <Link component={RouterLink} to="/privacy">
                {t("legalLinks.privacy")}
              </Link>
              .
            </Typography>
          }
        />
        <Typography variant="caption" color="text.secondary">
          {t("authRegister.legalVersions", {
            terms: CURRENT_TERMS_VERSION,
            privacy: CURRENT_PRIVACY_VERSION,
          })}
        </Typography>

        <Button
          type="submit"
          variant="contained"
          startIcon={<PersonAddAltIcon />}
          disabled={submitDisabled}
          fullWidth
        >
          {loading ? t("authRegister.creating") : t("authRegister.create")}
        </Button>

        <SocialSignInButtons returnTo={returnTo} />

        <Box
          sx={{
            mt: 0.5,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("auth.entry.signInPrompt")}
          </Typography>
          <Button
            component={RouterLink}
            to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
            variant="outlined"
            fullWidth
            sx={{ mt: 1 }}
          >
            {t("auth.entry.signInAction")}
          </Button>
        </Box>
      </Stack>
    </AuthPageShell>
  );
};

export default RegisterPage;
